import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PageHeroProps {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  image?: string;
  align?: "left" | "center";
  size?: "sm" | "md" | "lg";
  children?: ReactNode;
}

const PageHero = ({
  eyebrow,
  title,
  subtitle,
  image,
  align = "left",
  size = "md",
  children,
}: PageHeroProps) => {
  const heights = {
    sm: "min-h-[55vh] py-24",
    md: "min-h-[70vh] py-28",
    lg: "min-h-[85vh] py-32",
  };

  return (
    <section
      className={cn(
        "relative flex items-center pt-28 overflow-hidden isolate",
        heights[size],
      )}
    >
      <div className="absolute inset-0 z-0">
        {image ? (
          <img
            src={image}
            alt=""
            className="w-full h-full object-cover"
            width={1920}
            height={1080}
          />
        ) : (
          <div className="w-full h-full bg-gradient-hero" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--hero-bg))]/85 via-[hsl(var(--hero-bg))]/55 to-[hsl(var(--hero-bg))]/90" />
      </div>

      <div
        className={cn(
          "relative z-10 container mx-auto px-4 text-white",
          align === "center" && "text-center",
        )}
      >
        <div className={cn("max-w-3xl", align === "center" && "mx-auto")}>
          {eyebrow && (
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium tracking-[0.22em] uppercase">
              {eyebrow}
            </span>
          )}
          <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-6">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-5 text-lg text-white/80 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          )}
          {children && <div className="mt-8">{children}</div>}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
